import { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Plus, 
  ShoppingCart, 
  DollarSign, 
  TrendingUp, 
  Calculator,
  Printer,
  Download,
  Percent,
  Tag,
  Receipt,
  Trash2,
  Package,
  ScanLine,
  X,
  Star,
  CreditCard,
  Banknote,
  Wallet,
  Mail,
  MessageCircle,
  Pause,
  Play,
  RotateCcw,
  XCircle,
  CheckCircle,
  AlertTriangle,
  Bell
} from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import html2canvas from 'html2canvas';

import BarcodeScanner from '../components/BarcodeScanner';
export default function SalesPage() {
  const { user } = useAuth();
  const [sales, setSales] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [items, setItems] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [orgInfo, setOrgInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [lastSale, setLastSale] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const receiptRef = useRef(null);
  
  // Multi-item cart state
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [cart, setCart] = useState([]);
  const [currentItem, setCurrentItem] = useState({ item_id: '', quantity: 1, selling_price: 0 });
  const [itemSearch, setItemSearch] = useState('');
  const [showItemDropdown, setShowItemDropdown] = useState(false);
  const [discountType, setDiscountType] = useState('none');
  const [discountValue, setDiscountValue] = useState(0);
  const [amountPaid, setAmountPaid] = useState('');
  const [notes, setNotes] = useState('');
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [pointsToRedeem, setPointsToRedeem] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [heldTransactions, setHeldTransactions] = useState(() => {
    try {
      const saved = localStorage.getItem('jaminventra_held_transactions');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [showHeldList, setShowHeldList] = useState(false);
  const [voidDialogOpen, setVoidDialogOpen] = useState(false);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refundItems, setRefundItems] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [approvalsOpen, setApprovalsOpen] = useState(false);

  // Persist held transactions to localStorage
  useEffect(() => {
    localStorage.setItem('jaminventra_held_transactions', JSON.stringify(heldTransactions));
  }, [heldTransactions]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [salesRes, warehousesRes, itemsRes, inventoryRes, orgRes, customersRes] = await Promise.all([
        api.get('/sales?limit=50'),
        api.get('/warehouses'),
        api.get('/items'),
        api.get('/inventory'),
        api.get('/organization').catch(() => ({ data: null })),
        api.get('/customers').catch(() => ({ data: [] }))
      ]);
      setSales(salesRes.data);
      setWarehouses(warehousesRes.data);
      setItems(itemsRes.data);
      setInventory(inventoryRes.data);
      if (orgRes.data) setOrgInfo(orgRes.data);
      setCustomers(customersRes.data || []);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const getItemStock = (itemId, warehouseId) => {
    const inv = inventory.find(i => i.item_id === itemId && i.warehouse_id === warehouseId);
    return inv ? inv.quantity_on_hand : 0;
  };

  const getItemCost = (itemId, warehouseId) => {
    const inv = inventory.find(i => i.item_id === itemId && i.warehouse_id === warehouseId);
    return inv ? inv.avg_cost : 0;
  };

  // Handle barcode scan - find item by SKU or barcode
  const handleBarcodeScan = (barcode) => {
    if (!selectedWarehouse) {
      toast.error('Please select a warehouse first');
      setScannerOpen(false);
      return;
    }
    
    // Find item by SKU or barcode
    const item = items.find(i => 
      i.sku?.toLowerCase() === barcode.toLowerCase() || 
      i.barcode === barcode
    );
    
    if (!item) {
      toast.error(`Item not found: ${barcode}`);
      setScannerOpen(false);
      return;
    }
    
    const stock = getItemStock(item.id, selectedWarehouse);
    const cartQty = cart.filter(c => c.item_id === item.id).reduce((sum, c) => sum + c.quantity, 0);
    
    if (stock - cartQty <= 0) {
      toast.error(`${item.name} is out of stock`);
      setScannerOpen(false);
      return;
    }
    
    const cost = getItemCost(item.id, selectedWarehouse);
    const sellingPrice = item.selling_price || (cost ? Math.ceil(cost * 1.3) : 0);
    
    // Add to cart directly
    setCart([...cart, {
      item_id: item.id,
      item_sku: item.sku,
      item_name: item.name,
      quantity: 1,
      selling_price: sellingPrice,
      cost_price: cost,
      line_total: sellingPrice
    }]);
    
    toast.success(`${item.name} added to cart`);
    setScannerOpen(false);
  };

  const addToCart = () => {
    if (!currentItem.item_id || currentItem.quantity <= 0 || currentItem.selling_price <= 0) {
      toast.error('Please select item with valid quantity and price');
      return;
    }
    
    const item = items.find(i => i.id === currentItem.item_id);
    const stock = getItemStock(currentItem.item_id, selectedWarehouse);
    const cartQty = cart.filter(c => c.item_id === currentItem.item_id).reduce((sum, c) => sum + c.quantity, 0);
    
    if (currentItem.quantity + cartQty > stock) {
      toast.error(`Insufficient stock. Available: ${stock - cartQty}`);
      return;
    }
    
    const cost = getItemCost(currentItem.item_id, selectedWarehouse);
    
    setCart([...cart, {
      item_id: currentItem.item_id,
      item_sku: item.sku,
      item_name: item.name,
      quantity: currentItem.quantity,
      selling_price: currentItem.selling_price,
      cost_price: cost,
      line_total: currentItem.quantity * currentItem.selling_price
    }]);
    
    setCurrentItem({ item_id: '', quantity: 1, selling_price: 0 });
    setItemSearch('');
    toast.success(`${item.name} added to cart`);
  };

  const removeFromCart = (index) => {
    setCart(cart.filter((_, i) => i !== index));
  };

  const calculateTotals = () => {
    const subtotal = cart.reduce((sum, item) => sum + item.line_total, 0);
    let discountAmount = 0;
    
    if (discountValue > 0) {
      if (discountType === 'percentage') {
        discountAmount = subtotal * (discountValue / 100);
      } else if (discountType === 'fixed') {
        discountAmount = Math.min(discountValue, subtotal);
      }
    }
    
    const pointValue = orgInfo?.loyalty_point_value || 1;
    const pointsDiscount = pointsToRedeem * pointValue;
    
    const total = Math.max(0, subtotal - discountAmount - pointsDiscount);
    const totalCost = cart.reduce((sum, item) => sum + (item.cost_price * item.quantity), 0);
    const profit = total - totalCost;
    
    return { subtotal, discountAmount, pointsDiscount, total, profit };
  };

  const handleSubmit = async () => {
    if (!selectedWarehouse) {
      toast.error('Please select a warehouse');
      return;
    }
    
    if (cart.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    
    try {
      const payload = {
        warehouse_id: selectedWarehouse,
        items: cart.map(c => ({
          item_id: c.item_id,
          quantity: c.quantity,
          selling_price: c.selling_price
        })),
        discount_type: discountType === 'none' ? null : discountType,
        discount_value: discountType === 'none' ? 0 : discountValue,
        amount_paid: amountPaid ? parseFloat(amountPaid) : 0,
        payment_method: paymentMethod,
        customer_id: selectedCustomer?.id || null,
        customer_name: selectedCustomer?.name || null,
        customer_phone: selectedCustomer?.phone || null,
        points_redeemed: pointsToRedeem || 0,
        notes: notes
      };
      
      const response = await api.post('/sales', payload);
      toast.success('Sale completed!');
      setDialogOpen(false);
      setLastSale(response.data);
      setReceiptOpen(true);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sale failed');
    }
  };

  const resetForm = () => {
    setSelectedWarehouse('');
    setCart([]);
    setCurrentItem({ item_id: '', quantity: 1, selling_price: 0 });
    setItemSearch('');
    setShowItemDropdown(false);
    setDiscountType('none');
    setDiscountValue(0);
    setAmountPaid('');
    setNotes('');
    setSelectedCustomer(null);
    setCustomerSearch('');
    setPointsToRedeem(0);
    setPaymentMethod('cash');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-JM', {
      style: 'currency',
      currency: 'JMD',
      minimumFractionDigits: 2
    }).format(value).replace('JMD', 'J$');
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  const formatReceiptDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handlePrintReceipt = () => {
    const printContent = receiptRef.current;
    const printWindow = window.open('', '', 'width=400,height=600');
    const doc = printWindow.document;
    doc.open();
    doc.write('<html><head><title>Receipt - ' + (lastSale?.receipt_number || '') + '</title>');
    doc.write('<style>body { font-family: "Courier New", monospace; font-size: 12px; padding: 10px; margin: 0; width: 280px; } .header { text-align: center; margin-bottom: 10px; } .store-name { font-size: 16px; font-weight: bold; } .divider { border-top: 1px dashed #000; margin: 8px 0; } .row { display: flex; justify-content: space-between; margin: 4px 0; } .item-row { margin: 8px 0; padding-bottom: 8px; border-bottom: 1px dotted #ccc; } .item-name { font-weight: bold; } .item-details { color: #666; font-size: 11px; } .total-row { font-weight: bold; font-size: 14px; } .footer { text-align: center; margin-top: 15px; font-size: 11px; } @media print { body { width: 80mm; } }</style>');
    doc.write('</head><body>');
    doc.write(printContent.innerHTML);
    doc.write('</body></html>');
    doc.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const handleDownloadPDF = () => {
    const printContent = receiptRef.current;
    const printWindow = window.open('', '', 'width=400,height=600');
    const doc = printWindow.document;
    doc.open();
    doc.write('<html><head><title>Receipt - ' + (lastSale?.receipt_number || '') + '</title>');
    doc.write('<style>body { font-family: "Courier New", monospace; font-size: 12px; padding: 20px; margin: 0; max-width: 300px; } .header { text-align: center; margin-bottom: 15px; } .store-name { font-size: 18px; font-weight: bold; } .divider { border-top: 1px dashed #000; margin: 10px 0; } .row { display: flex; justify-content: space-between; margin: 5px 0; } .item-row { margin: 10px 0; padding-bottom: 8px; border-bottom: 1px dotted #ccc; } .item-name { font-weight: bold; } .item-details { color: #666; font-size: 11px; } .total-row { font-weight: bold; font-size: 16px; } .footer { text-align: center; margin-top: 20px; font-size: 11px; }</style>');
    doc.write('</head><body>');
    doc.write(printContent.innerHTML);
    doc.write('<script>window.onload = function() { window.print(); }<\/script>');
    doc.write('</body></html>');
    doc.close();
    toast.info('Use "Save as PDF" in the print dialog');
  };

  const viewReceipt = (sale) => {
    setLastSale(sale);
    setReceiptOpen(true);
  };

  const handleEmailReceipt = async () => {
    if (!lastSale) return;
    
    // Try to find customer email automatically
    const autoEmail = customers.find(c => c.name === lastSale.customer_name)?.email;
    
    // Always prompt — pre-fill with customer email if found
    const emailToSend = prompt('Enter email to send receipt to:', autoEmail || '');
    if (!emailToSend || !emailToSend.trim()) return;
    
    setSendingEmail(true);
    try {
      await api.post('/sales/email-receipt', { sale_id: lastSale.id, email: emailToSend.trim() });
      toast.success(`Receipt sent to ${emailToSend.trim()}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleWhatsAppReceipt = async () => {
    if (!lastSale || !receiptRef.current) return;
    
    // Try Web Share API with receipt image (works on mobile)
    try {
      const canvas = await html2canvas(receiptRef.current, { scale: 2, backgroundColor: '#ffffff' });
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      const file = new File([blob], `${lastSale.receipt_number || 'receipt'}.png`, { type: 'image/png' });
      
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: `Receipt ${lastSale.receipt_number}`,
          text: `Receipt from ${orgInfo?.name || 'Store'} - Total: ${formatCurrency(lastSale.total_amount)}`,
          files: [file]
        });
        return;
      }
    } catch (e) {
      // Web Share API not supported on this device, use wa.me fallback
    }
    
    // Fallback: open WhatsApp with text receipt
    const storeName = orgInfo?.name || user?.organization_name || 'Store';
    const itemsList = lastSale.items?.map(i => `${i.quantity}x ${i.item_name} - ${formatCurrency(i.line_total)}`).join('\n') || '';
    let text = `*${storeName}*\n`;
    if (orgInfo?.address) text += `${orgInfo.address}\n`;
    if (orgInfo?.phone) text += `Tel: ${orgInfo.phone}${orgInfo.phone2 ? ' / ' + orgInfo.phone2 : ''}\n`;
    text += `\nReceipt: ${lastSale.receipt_number}\n`;
    text += `Date: ${formatReceiptDate(lastSale.created_at)}\n`;
    text += `━━━━━━━━━━━━━━━\n${itemsList}\n━━━━━━━━━━━━━━━\n`;
    if (lastSale.discount_amount > 0) text += `Discount: -${formatCurrency(lastSale.discount_amount)}\n`;
    text += `*TOTAL: ${formatCurrency(lastSale.total_amount)}*\n`;
    if (lastSale.amount_paid > 0) {
      text += `Paid: ${formatCurrency(lastSale.amount_paid)}\n`;
      text += `Change: ${formatCurrency(lastSale.amount_paid - lastSale.total_amount)}\n`;
    }
    text += `Payment: ${(lastSale.payment_method || 'cash').toUpperCase()}\n`;
    if (lastSale.points_earned > 0) text += `\nPoints Earned: +${lastSale.points_earned}`;
    if (lastSale.customer_points_balance > 0) text += `\nPoints Balance: ${lastSale.customer_points_balance}`;
    if (orgInfo?.return_policy) text += `\n\n_${orgInfo.return_policy}_`;
    text += `\n\n${orgInfo?.receipt_tagline || 'Thank you for your purchase!'}`;

    const customerPhone = lastSale.customer_phone || customers.find(c => c.name === lastSale.customer_name)?.phone;
    const cleanPhone = customerPhone ? customerPhone.replace(/[^0-9]/g, '') : '';
    const waPhone = cleanPhone.startsWith('1876') ? cleanPhone : cleanPhone.startsWith('876') ? `1${cleanPhone}` : cleanPhone;
    
    const url = waPhone 
      ? `https://wa.me/${waPhone}?text=${encodeURIComponent(text)}`
      : `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  const handleHoldTransaction = () => {
    if (cart.length === 0) {
      toast.error('Nothing to hold — cart is empty');
      return;
    }
    const held = {
      id: Date.now(),
      cart: [...cart],
      selectedWarehouse,
      selectedCustomer,
      customerSearch,
      discountType,
      discountValue,
      amountPaid,
      paymentMethod,
      pointsToRedeem,
      notes,
      heldAt: new Date().toISOString(),
      label: selectedCustomer?.name || `Sale #${heldTransactions.length + 1}`,
      total: cart.reduce((sum, c) => sum + (c.selling_price * c.quantity), 0),
    };
    setHeldTransactions(prev => [...prev, held]);
    resetForm();
    setDialogOpen(false);
    toast.success(`Transaction held: ${held.label}`);
  };

  const handleRecallTransaction = (held) => {
    // If current cart has items, hold it first
    if (cart.length > 0) {
      const currentHeld = {
        id: Date.now(),
        cart: [...cart],
        selectedWarehouse,
        selectedCustomer,
        customerSearch,
        discountType,
        discountValue,
        amountPaid,
        paymentMethod,
        pointsToRedeem,
        notes,
        heldAt: new Date().toISOString(),
        label: selectedCustomer?.name || `Sale #${heldTransactions.length + 1}`,
        total: cart.reduce((sum, c) => sum + (c.selling_price * c.quantity), 0),
      };
      setHeldTransactions(prev => [...prev.filter(h => h.id !== held.id), currentHeld]);
    } else {
      setHeldTransactions(prev => prev.filter(h => h.id !== held.id));
    }

    // Restore the recalled transaction
    setCart(held.cart);
    setSelectedWarehouse(held.selectedWarehouse);
    setSelectedCustomer(held.selectedCustomer);
    setCustomerSearch(held.customerSearch || '');
    setDiscountType(held.discountType || 'none');
    setDiscountValue(held.discountValue || 0);
    setAmountPaid(held.amountPaid || '');
    setPaymentMethod(held.paymentMethod || 'cash');
    setPointsToRedeem(held.pointsToRedeem || 0);
    setNotes(held.notes || '');
    setShowHeldList(false);
    setDialogOpen(true);
    toast.success(`Recalled: ${held.label}`);
  };

  const handleDeleteHeld = (heldId) => {
    setHeldTransactions(prev => prev.filter(h => h.id !== heldId));
    toast.success('Held transaction removed');
  };

  const fetchPendingApprovals = async () => {
    try {
      const res = await api.get('/sales/pending-approvals');
      setPendingApprovals(res.data);
    } catch { /* non-manager role — expected 403 */ }
  };

  useEffect(() => { fetchPendingApprovals(); }, [sales]);

  const handleRequestVoid = async () => {
    if (!voidReason.trim()) { toast.error('Please enter a reason'); return; }
    try {
      const res = await api.post(`/sales/${lastSale.id}/request-void`, { reason: voidReason });
      toast.success(res.data.message);
      setVoidDialogOpen(false);
      setVoidReason('');
      setReceiptOpen(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openRefundDialog = () => {
    if (!lastSale) return;
    const items = (lastSale.items || []).map(i => ({
      ...i, selected: false, refund_qty: i.quantity,
      max_qty: i.quantity // TODO: subtract already refunded
    }));
    setRefundItems(items);
    setRefundReason('');
    setRefundDialogOpen(true);
  };

  const handleRequestRefund = async () => {
    if (!refundReason.trim()) { toast.error('Please enter a reason'); return; }
    const selected = refundItems.filter(i => i.selected && i.refund_qty > 0);
    if (selected.length === 0) { toast.error('Select items to refund'); return; }
    try {
      const res = await api.post(`/sales/${lastSale.id}/request-refund`, {
        items: selected.map(i => ({ item_id: i.item_id, quantity: i.refund_qty })),
        reason: refundReason
      });
      toast.success(res.data.message);
      setRefundDialogOpen(false);
      setReceiptOpen(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleApproveVoid = async (saleId) => {
    try {
      const res = await api.post(`/sales/${saleId}/approve-void`);
      toast.success(res.data.message);
      fetchData(); fetchPendingApprovals();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleRejectVoid = async (saleId) => {
    try {
      const res = await api.post(`/sales/${saleId}/reject-void`);
      toast.success(res.data.message);
      fetchData(); fetchPendingApprovals();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleApproveRefund = async (saleId, refundId) => {
    try {
      const res = await api.post(`/sales/${saleId}/approve-refund/${refundId}`);
      toast.success(res.data.message);
      fetchData(); fetchPendingApprovals();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleRejectRefund = async (saleId, refundId) => {
    try {
      const res = await api.post(`/sales/${saleId}/reject-refund/${refundId}`);
      toast.success(res.data.message);
      fetchData(); fetchPendingApprovals();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // Calculate summary stats
  const totalRevenue = sales.reduce((sum, s) => sum + s.subtotal, 0);
  const totalProfit = sales.reduce((sum, s) => sum + s.profit, 0);
  const totalSales = sales.length;
  const totals = calculateTotals();

  return (
    <div className="space-y-6" data-testid="sales-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-heading-black text-3xl lg:text-4xl text-[#0A0A0A] tracking-tight">SALES</h1>
          <p className="text-[#0A0A0A]/60 mt-1">Record sales and track profit</p>
        </div>
        <div className="flex gap-2">
          {pendingApprovals.length > 0 && (
            <Button 
              variant="outline" 
              className="border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/5 relative"
              onClick={() => setApprovalsOpen(true)}
              data-testid="pending-approvals-btn"
            >
              <Bell className="w-4 h-4 mr-2" />
              Approvals ({pendingApprovals.length})
            </Button>
          )}
          {heldTransactions.length > 0 && (
            <Button 
              variant="outline"
              className="border-[#FF9500] text-[#FF9500] hover:bg-[#FF9500]/5 relative"
              onClick={() => setShowHeldList(true)}
              data-testid="recall-sale-btn"
            >
              <Play className="w-4 h-4 mr-2" />
              Recall ({heldTransactions.length})
            </Button>
          )}
          <Button 
            className="bg-[#34C759] hover:bg-[#2DB14E]" 
            onClick={() => { resetForm(); setDialogOpen(true); }}
            data-testid="new-sale-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Sale
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border border-[#E5E5EA] shadow-none">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-overline text-[#0A0A0A]/50 mb-2">Total Sales</p>
                <p className="kpi-number text-3xl text-[#0A0A0A]">{totalSales}</p>
              </div>
              <div className="w-10 h-10 bg-[#007AFF]/10 rounded-sm flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-[#007AFF]" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-[#E5E5EA] shadow-none">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-overline text-[#0A0A0A]/50 mb-2">Revenue</p>
                <p className="kpi-number text-2xl text-[#0A0A0A]">{formatCurrency(totalRevenue)}</p>
              </div>
              <div className="w-10 h-10 bg-[#007AFF]/10 rounded-sm flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-[#007AFF]" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-[#E5E5EA] shadow-none">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-overline text-[#0A0A0A]/50 mb-2">Profit</p>
                <p className={`kpi-number text-2xl ${totalProfit >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>
                  {formatCurrency(totalProfit)}
                </p>
              </div>
              <div className="w-10 h-10 bg-[#34C759]/10 rounded-sm flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-[#34C759]" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sales Table */}
      <Card className="border border-[#E5E5EA] shadow-none">
        <CardHeader className="border-b border-[#E5E5EA] pb-4">
          <CardTitle className="text-heading text-lg flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-[#007AFF]" />
            Recent Sales
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#007AFF]"></div>
            </div>
          ) : sales.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <ShoppingCart className="w-12 h-12 text-[#0A0A0A]/20 mb-4" />
              <p className="text-[#0A0A0A]/60">No sales recorded yet</p>
              <Button 
                className="mt-4 bg-[#34C759] hover:bg-[#2DB14E]"
                onClick={() => { resetForm(); setDialogOpen(true); }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Record Your First Sale
              </Button>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-[#E5E5EA]">
                      <TableHead className="text-overline text-[#0A0A0A]/50">Receipt #</TableHead>
                      <TableHead className="text-overline text-[#0A0A0A]/50">Items</TableHead>
                      <TableHead className="text-overline text-[#0A0A0A]/50 text-right">Subtotal</TableHead>
                      <TableHead className="text-overline text-[#0A0A0A]/50 text-right">Discount</TableHead>
                      <TableHead className="text-overline text-[#0A0A0A]/50 text-right">Total</TableHead>
                      <TableHead className="text-overline text-[#0A0A0A]/50 text-center">Payment</TableHead>
                      <TableHead className="text-overline text-[#0A0A0A]/50 text-right">Profit</TableHead>
                      <TableHead className="text-overline text-[#0A0A0A]/50"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sales.map((sale) => (
                      <TableRow key={sale.id} className="border-b border-[#E5E5EA]" data-testid={`sale-row-${sale.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-mono text-sm font-medium">{sale.receipt_number || '-'}</p>
                            <p className="text-xs text-[#0A0A0A]/50">{formatDate(sale.created_at)}</p>
                            {sale.status && sale.status !== 'completed' && (
                              <span className={`inline-block text-[9px] font-bold px-1.5 py-0.5 rounded mt-0.5 ${
                                sale.status === 'voided' ? 'bg-[#FF3B30]/10 text-[#FF3B30]' :
                                sale.status.includes('refund') ? 'bg-[#FF9500]/10 text-[#FF9500]' :
                                sale.status === 'void_requested' ? 'bg-[#FF3B30]/10 text-[#FF3B30]' :
                                'bg-gray-100 text-gray-500'
                              }`}>{sale.status.replace(/_/g, ' ').toUpperCase()}</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="max-w-xs">
                            {sale.items?.slice(0, 2).map((item, idx) => (
                              <p key={idx} className="text-sm truncate">
                                {item.quantity}x {item.item_name}
                              </p>
                            ))}
                            {sale.items?.length > 2 && (
                              <p className="text-xs text-[#0A0A0A]/50">+{sale.items.length - 2} more</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(sale.subtotal)}</TableCell>
                        <TableCell className="text-right font-mono text-[#FF9500]">
                          {sale.discount_amount > 0 ? `-${formatCurrency(sale.discount_amount)}` : '-'}
                        </TableCell>
                        <TableCell className="text-right font-mono font-medium">
                          {formatCurrency(sale.total_amount)}
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                            (sale.payment_method || 'cash') === 'cash' ? 'bg-[#34C759]/10 text-[#34C759]' :
                            (sale.payment_method || 'cash') === 'card' ? 'bg-[#007AFF]/10 text-[#007AFF]' :
                            'bg-[#FF9500]/10 text-[#FF9500]'
                          }`}>
                            {(sale.payment_method || 'cash') === 'cash' && <Banknote className="w-3 h-3" />}
                            {(sale.payment_method || 'cash') === 'card' && <CreditCard className="w-3 h-3" />}
                            {(sale.payment_method || 'cash') === 'credit' && <Wallet className="w-3 h-3" />}
                            {(sale.payment_method || 'Cash').charAt(0).toUpperCase() + (sale.payment_method || 'cash').slice(1)}
                          </span>
                        </TableCell>
                        <TableCell className={`text-right font-mono font-medium ${sale.profit >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>
                          {formatCurrency(sale.profit)}
                        </TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => viewReceipt(sale)}
                            data-testid={`view-receipt-${sale.id}`}
                          >
                            <Receipt className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {/* Mobile Cards */}
              <div className="md:hidden divide-y divide-[#E5E5EA]">
                {sales.map((sale) => (
                  <div key={sale.id} className="p-4 space-y-3" data-testid={`sale-card-${sale.id}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-mono text-sm font-medium">{sale.receipt_number}</p>
                        <p className="text-xs text-[#0A0A0A]/50">{sale.items?.length} item(s)</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`font-mono font-medium ${sale.profit >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>
                          {formatCurrency(sale.profit)}
                        </span>
                        <Button variant="ghost" size="sm" onClick={() => viewReceipt(sale)}>
                          <Receipt className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#0A0A0A]/50">{formatDate(sale.created_at)}</span>
                      <span className="font-mono font-medium">Total: {formatCurrency(sale.total_amount)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* New Sale Dialog - Multi-item */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-heading text-xl flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-[#34C759]" />
              New Sale
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Warehouse Selection */}
            <div className="space-y-2">
              <Label>Warehouse *</Label>
              <Select 
                value={selectedWarehouse} 
                onValueChange={(v) => { setSelectedWarehouse(v); setCart([]); }}
              >
                <SelectTrigger data-testid="sale-warehouse-select">
                  <SelectValue placeholder="Select warehouse" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map((wh) => (
                    <SelectItem key={wh.id} value={wh.id}>{wh.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Customer Selection */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Customer (Optional)</Label>
              {selectedCustomer ? (
                <div className="flex items-center justify-between p-2 bg-[#007AFF]/5 border border-[#007AFF]/20 rounded-md">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-[#007AFF]/10 flex items-center justify-center text-[#007AFF] font-bold text-xs">
                      {selectedCustomer.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <span className="text-sm font-medium">{selectedCustomer.name}</span>
                      <span className="text-xs text-[#0A0A0A]/40 ml-2 font-mono">{selectedCustomer.account_number}</span>
                      {orgInfo?.loyalty_feature_enabled && selectedCustomer.loyalty_enabled && <span className="text-xs text-[#FFD700] ml-2">{selectedCustomer.points || 0} pts</span>}
                    </div>
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setSelectedCustomer(null)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="relative">
                  <Input
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    placeholder="Search customer by name or phone..."
                    className="pr-8"
                    data-testid="customer-search-input"
                  />
                  {customerSearch && (
                    <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-[#E5E5EA] rounded-md shadow-lg max-h-40 overflow-y-auto">
                      {customers.filter(c => 
                        c.name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
                        c.phone?.includes(customerSearch) ||
                        c.account_number?.includes(customerSearch)
                      ).map(c => (
                        <div key={c.id} className="p-2 hover:bg-[#F5F5F7] cursor-pointer flex items-center justify-between" onClick={() => { setSelectedCustomer(c); setCustomerSearch(''); }} data-testid={`select-customer-${c.id}`}>
                          <div>
                            <span className="text-sm font-medium">{c.name}</span>
                            <span className="text-xs text-[#0A0A0A]/40 ml-2">{c.phone}</span>
                          </div>
                          <span className="text-xs text-[#FFD700]">{c.points || 0} pts</span>
                        </div>
                      ))}
                      {customers.filter(c => c.name?.toLowerCase().includes(customerSearch.toLowerCase()) || c.phone?.includes(customerSearch)).length === 0 && (
                        <div className="p-2 text-sm text-[#0A0A0A]/40">No customer found</div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Add Item Section */}
            {selectedWarehouse && (
              <Card className="border border-dashed border-[#007AFF]/30 bg-[#007AFF]/5">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-sm font-medium">Add Item to Cart</Label>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => setScannerOpen(true)}
                      className="text-[#007AFF] border-[#007AFF]"
                      data-testid="scan-barcode-btn"
                    >
                      <ScanLine className="w-4 h-4 mr-2" />
                      Scan Barcode
                    </Button>
                  </div>
                  <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-5 relative">
                      <Input
                        placeholder="Search item by name or SKU..."
                        value={itemSearch}
                        onChange={(e) => {
                          setItemSearch(e.target.value);
                          setShowItemDropdown(true);
                        }}
                        onFocus={() => setShowItemDropdown(true)}
                        onBlur={() => setTimeout(() => setShowItemDropdown(false), 200)}
                        data-testid="sale-item-search"
                      />
                      {showItemDropdown && itemSearch.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-[#E5E5EA] rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {items
                            .filter(item => {
                              const q = itemSearch.toLowerCase();
                              return item.name.toLowerCase().includes(q) || item.sku.toLowerCase().includes(q) || (item.barcode && item.barcode.includes(q));
                            })
                            .map((item) => {
                              const stock = getItemStock(item.id, selectedWarehouse);
                              const cartQty = cart.filter(c => c.item_id === item.id).reduce((sum, c) => sum + c.quantity, 0);
                              const available = stock - cartQty;
                              return (
                                <button
                                  key={item.id}
                                  type="button"
                                  disabled={available <= 0}
                                  className={`w-full text-left px-3 py-2 text-sm hover:bg-[#007AFF]/5 transition-colors border-b border-[#E5E5EA] last:border-0 ${available <= 0 ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                                  onClick={() => {
                                    setCurrentItem({
                                      ...currentItem,
                                      item_id: item.id,
                                      selling_price: item.selling_price || item.cost_price || 0
                                    });
                                    setItemSearch(`${item.sku} - ${item.name}`);
                                    setShowItemDropdown(false);
                                  }}
                                  data-testid={`item-option-${item.id}`}
                                >
                                  <div className="font-medium">{item.sku} - {item.name}</div>
                                  <div className="text-xs text-[#0A0A0A]/50">
                                    Stock: {available} • {formatCurrency(item.selling_price || 0)}
                                  </div>
                                </button>
                              );
                            })
                          }
                          {items.filter(item => {
                            const q = itemSearch.toLowerCase();
                            return item.name.toLowerCase().includes(q) || item.sku.toLowerCase().includes(q);
                          }).length === 0 && (
                            <div className="px-3 py-4 text-center text-sm text-[#0A0A0A]/40">No items found</div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        placeholder="Qty"
                        value={currentItem.quantity}
                        onChange={(e) => setCurrentItem({ ...currentItem, quantity: parseInt(e.target.value) || 1 })}
                        min="1"
                        data-testid="sale-quantity-input"
                      />
                    </div>
                    <div className="col-span-3">
                      <Input
                        type="number"
                        placeholder="Price"
                        value={currentItem.selling_price}
                        onChange={(e) => setCurrentItem({ ...currentItem, selling_price: parseFloat(e.target.value) || 0 })}
                        min="0"
                        step="0.01"
                        data-testid="sale-price-input"
                      />
                    </div>
                    <div className="col-span-2">
                      <Button 
                        type="button" 
                        className="w-full bg-[#007AFF]" 
                        onClick={addToCart}
                        data-testid="add-to-cart-btn"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Cart Items */}
            {cart.length > 0 && (
              <Card className="border border-[#E5E5EA]">
                <CardHeader className="py-3 px-4 border-b border-[#E5E5EA]">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Cart ({cart.length} items)
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Item</TableHead>
                        <TableHead className="text-xs text-right">Qty</TableHead>
                        <TableHead className="text-xs text-right">Price</TableHead>
                        <TableHead className="text-xs text-right">Total</TableHead>
                        <TableHead className="text-xs w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cart.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell className="py-2">
                            <p className="font-medium text-sm">{item.item_name}</p>
                            <p className="text-xs text-[#0A0A0A]/50">{item.item_sku}</p>
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm">{item.quantity}</TableCell>
                          <TableCell className="text-right font-mono text-sm">{formatCurrency(item.selling_price)}</TableCell>
                          <TableCell className="text-right font-mono text-sm font-medium">{formatCurrency(item.line_total)}</TableCell>
                          <TableCell>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => removeFromCart(index)}
                              className="text-[#FF3B30] h-8 w-8 p-0"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {/* Discount */}
            {cart.length > 0 && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Tag className="w-4 h-4 text-[#FF9500]" />
                  Discount
                </Label>
                <div className="grid grid-cols-2 gap-4">
                  <Select value={discountType} onValueChange={setDiscountType}>
                    <SelectTrigger data-testid="sale-discount-type">
                      <SelectValue placeholder="No discount" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Discount</SelectItem>
                      <SelectItem value="percentage"><Percent className="w-3 h-3 inline mr-2" />Percentage</SelectItem>
                      <SelectItem value="fixed"><DollarSign className="w-3 h-3 inline mr-2" />Fixed Amount</SelectItem>
                    </SelectContent>
                  </Select>
                  {discountType !== 'none' && (
                    <Input
                      type="number"
                      value={discountValue}
                      onChange={(e) => setDiscountValue(parseFloat(e.target.value) || 0)}
                      min="0"
                      placeholder={discountType === 'percentage' ? '% off' : 'J$ off'}
                      data-testid="sale-discount-value"
                    />
                  )}
                </div>
              </div>
            )}

            {/* Apply Loyalty Points */}
            {cart.length > 0 && selectedCustomer && orgInfo?.loyalty_feature_enabled && selectedCustomer.loyalty_enabled && (selectedCustomer.points || 0) > 0 && (
              <div className="space-y-2 bg-[#FFD700]/5 border border-[#FFD700]/20 rounded-lg p-3">
                <Label className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-[#FFD700]" />
                  Loyalty Points
                  <span className="ml-auto text-xs font-normal text-[#0A0A0A]/50">
                    {selectedCustomer.name}: {selectedCustomer.points} pts available (= J${((selectedCustomer.points || 0) * (orgInfo?.loyalty_point_value || 1)).toLocaleString()})
                  </span>
                </Label>
                {pointsToRedeem > 0 ? (
                  <div className="flex items-center justify-between bg-[#FFD700]/10 rounded p-2">
                    <span className="text-sm font-medium">
                      Redeeming {pointsToRedeem} pts = <span className="text-[#34C759] font-bold">-J${(pointsToRedeem * (orgInfo?.loyalty_point_value || 1)).toLocaleString()}</span>
                    </span>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setPointsToRedeem(0)} className="text-[#FF3B30] h-7">
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="flex-1 border-[#FFD700] text-[#D4A800] hover:bg-[#FFD700]/10"
                      onClick={() => {
                        const minRedeem = orgInfo?.loyalty_min_redeem || 500;
                        const available = selectedCustomer.points || 0;
                        if (available < minRedeem) {
                          toast.error(`Need at least ${minRedeem} points to redeem. Customer has ${available}.`);
                          return;
                        }
                        setPointsToRedeem(Math.min(available, Math.ceil(calculateTotals().subtotal / (orgInfo?.loyalty_point_value || 1))));
                      }}
                      data-testid="apply-all-points-btn"
                    >
                      Apply All ({selectedCustomer.points} pts)
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="flex-1 border-[#FFD700] text-[#D4A800] hover:bg-[#FFD700]/10"
                      onClick={() => {
                        const minRedeem = orgInfo?.loyalty_min_redeem || 500;
                        const available = selectedCustomer.points || 0;
                        if (available < minRedeem) {
                          toast.error(`Need at least ${minRedeem} points to redeem. Customer has ${available}.`);
                          return;
                        }
                        setPointsToRedeem(minRedeem);
                      }}
                      data-testid="apply-min-points-btn"
                    >
                      Apply {orgInfo?.loyalty_min_redeem || 500} pts (J${((orgInfo?.loyalty_min_redeem || 500) * (orgInfo?.loyalty_point_value || 1)).toLocaleString()})
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Payment Method */}
            {cart.length > 0 && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Wallet className="w-4 h-4 text-[#007AFF]" />
                  Payment Method
                </Label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('cash')}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all ${
                      paymentMethod === 'cash' 
                        ? 'border-[#34C759] bg-[#34C759]/5 text-[#34C759]' 
                        : 'border-[#E5E5EA] hover:border-[#007AFF]/30 text-[#0A0A0A]/60'
                    }`}
                    data-testid="payment-method-cash"
                  >
                    <Banknote className="w-5 h-5" />
                    <span className="text-xs font-medium">Cash</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('card')}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all ${
                      paymentMethod === 'card' 
                        ? 'border-[#007AFF] bg-[#007AFF]/5 text-[#007AFF]' 
                        : 'border-[#E5E5EA] hover:border-[#007AFF]/30 text-[#0A0A0A]/60'
                    }`}
                    data-testid="payment-method-card"
                  >
                    <CreditCard className="w-5 h-5" />
                    <span className="text-xs font-medium">Card</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!selectedCustomer) {
                        toast.error('Select a customer first for credit payment');
                        return;
                      }
                      if ((selectedCustomer.credit_limit || 0) <= 0) {
                        toast.error('Customer has no credit limit set');
                        return;
                      }
                      setPaymentMethod('credit');
                    }}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all ${
                      paymentMethod === 'credit' 
                        ? 'border-[#FF9500] bg-[#FF9500]/5 text-[#FF9500]' 
                        : 'border-[#E5E5EA] hover:border-[#007AFF]/30 text-[#0A0A0A]/60'
                    }`}
                    data-testid="payment-method-credit"
                  >
                    <Wallet className="w-5 h-5" />
                    <span className="text-xs font-medium">Credit</span>
                  </button>
                </div>
                {paymentMethod === 'credit' && selectedCustomer && (
                  <div className="text-xs bg-[#FF9500]/5 border border-[#FF9500]/20 rounded p-2 space-y-1">
                    <div className="flex justify-between">
                      <span className="text-[#0A0A0A]/60">Credit Limit:</span>
                      <span className="font-mono font-medium">{formatCurrency(selectedCustomer.credit_limit || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#0A0A0A]/60">Outstanding Balance:</span>
                      <span className="font-mono font-medium text-[#FF3B30]">{formatCurrency(selectedCustomer.credit_balance || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#0A0A0A]/60">Available Credit:</span>
                      <span className="font-mono font-medium text-[#34C759]">{formatCurrency((selectedCustomer.credit_limit || 0) - (selectedCustomer.credit_balance || 0))}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Notes */}
            {cart.length > 0 && (
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Additional notes..."
                  rows={2}
                />
              </div>
            )}

            {/* Totals Preview */}
            {cart.length > 0 && (
              <Card className="border border-[#E5E5EA] bg-[#F5F5F7]">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Calculator className="w-4 h-4 text-[#007AFF]" />
                    <span className="text-sm font-medium">Order Summary</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#0A0A0A]/60">Subtotal ({cart.length} items):</span>
                      <span className="font-mono">{formatCurrency(totals.subtotal)}</span>
                    </div>
                    {totals.discountAmount > 0 && (
                      <div className="flex justify-between text-[#FF9500]">
                        <span>Discount:</span>
                        <span className="font-mono">-{formatCurrency(totals.discountAmount)}</span>
                      </div>
                    )}
                    {totals.pointsDiscount > 0 && (
                      <div className="flex justify-between text-[#FFD700]">
                        <span>Points Redeemed ({pointsToRedeem} pts):</span>
                        <span className="font-mono">-{formatCurrency(totals.pointsDiscount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-medium border-t pt-2 text-lg">
                      <span>Total:</span>
                      <span className="font-mono">{formatCurrency(totals.total)}</span>
                    </div>
                    <div className={`flex justify-between font-medium ${totals.profit >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>
                      <span>Profit:</span>
                      <span className="font-mono">{formatCurrency(totals.profit)}</span>
                    </div>
                    <div className="border-t pt-3 mt-3 space-y-2">
                      <div>
                        <Label className="text-xs text-[#0A0A0A]/60">Amount Paid (J$)</Label>
                        <Input
                          type="number"
                          value={amountPaid}
                          onChange={(e) => setAmountPaid(e.target.value)}
                          placeholder={`${Math.ceil(totals.total)}`}
                          min="0"
                          step="0.01"
                          className="font-mono mt-1"
                          data-testid="amount-paid-input"
                        />
                      </div>
                      {amountPaid && parseFloat(amountPaid) >= totals.total && (
                        <div className="flex justify-between font-bold text-lg text-[#007AFF] bg-[#007AFF]/10 p-2 rounded">
                          <span>Change:</span>
                          <span className="font-mono">{formatCurrency(parseFloat(amountPaid) - totals.total)}</span>
                        </div>
                      )}
                      {amountPaid && parseFloat(amountPaid) > 0 && parseFloat(amountPaid) < totals.total && (
                        <div className="flex justify-between font-bold text-sm text-[#FF3B30] bg-[#FF3B30]/10 p-2 rounded">
                          <span>Remaining:</span>
                          <span className="font-mono">{formatCurrency(totals.total - parseFloat(amountPaid))}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <DialogFooter className="mt-4 flex gap-2">
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            {cart.length > 0 && (
              <Button 
                type="button"
                variant="outline"
                className="border-[#FF9500] text-[#FF9500] hover:bg-[#FF9500]/5"
                onClick={handleHoldTransaction}
                data-testid="hold-sale-btn"
              >
                <Pause className="w-4 h-4 mr-2" />
                Hold
              </Button>
            )}
            <Button 
              onClick={handleSubmit} 
              className="bg-[#34C759] hover:bg-[#2DB14E]" 
              disabled={cart.length === 0}
              data-testid="complete-sale-btn"
            >
              Complete Sale
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receipt Dialog */}
      <Dialog open={receiptOpen} onOpenChange={setReceiptOpen}>
        <DialogContent className="max-w-md max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-heading text-xl flex items-center gap-2">
              <Receipt className="w-5 h-5 text-[#34C759]" />
              Sale Receipt
            </DialogTitle>
          </DialogHeader>
          
          {lastSale && (
            <>
              {/* Share buttons ABOVE receipt - always visible */}
              <div className="flex flex-wrap gap-2 pb-2">
                <Button size="sm" variant="outline" className="border-[#25D366] text-[#25D366] hover:bg-[#25D366]/5" onClick={handleWhatsAppReceipt} data-testid="whatsapp-receipt-btn">
                  <MessageCircle className="w-4 h-4 mr-1" />WhatsApp
                </Button>
                <Button size="sm" variant="outline" onClick={handleEmailReceipt} disabled={sendingEmail} data-testid="email-receipt-btn">
                  <Mail className="w-4 h-4 mr-1" />{sendingEmail ? 'Sending...' : 'Email'}
                </Button>
                <Button size="sm" variant="outline" onClick={handleDownloadPDF} data-testid="download-receipt-btn">
                  <Download className="w-4 h-4 mr-1" />PDF
                </Button>
                <Button size="sm" className="bg-[#007AFF] hover:bg-[#0066CC]" onClick={handlePrintReceipt} data-testid="print-receipt-btn">
                  <Printer className="w-4 h-4 mr-1" />Print
                </Button>
              </div>
              {lastSale.status !== 'voided' && lastSale.status !== 'refunded' && (
                <div className="flex gap-2 pb-2 border-b border-[#E5E5EA]">
                  <Button size="sm" variant="outline" className="flex-1 border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/5" onClick={() => { setVoidReason(''); setVoidDialogOpen(true); }} data-testid="void-sale-btn">
                    <XCircle className="w-4 h-4 mr-1" />Cancel / Void Sale
                  </Button>
                  <Button size="sm" variant="outline" className="flex-1 border-[#FF9500] text-[#FF9500] hover:bg-[#FF9500]/5" onClick={openRefundDialog} data-testid="refund-sale-btn">
                    <RotateCcw className="w-4 h-4 mr-1" />Refund Items
                  </Button>
                </div>
              )}
              {/* Status badge */}
              {lastSale.status && lastSale.status !== 'completed' && (
                <div className={`text-center text-xs font-bold py-1.5 rounded ${
                  lastSale.status === 'voided' ? 'bg-[#FF3B30]/10 text-[#FF3B30]' :
                  lastSale.status === 'refunded' ? 'bg-[#FF9500]/10 text-[#FF9500]' :
                  lastSale.status === 'partially_refunded' ? 'bg-[#FF9500]/10 text-[#FF9500]' :
                  lastSale.status === 'void_requested' ? 'bg-[#FF3B30]/10 text-[#FF3B30]' :
                  lastSale.status === 'refund_requested' ? 'bg-[#FF9500]/10 text-[#FF9500]' :
                  'bg-gray-100 text-gray-600'
                }`} data-testid="sale-status-badge">
                  {lastSale.status.replace(/_/g, ' ').toUpperCase()}
                  {lastSale.total_refunded > 0 && ` (${formatCurrency(lastSale.total_refunded)})`}
                </div>
              )}

              {/* Scrollable receipt content */}
              <div className="overflow-y-auto flex-1 max-h-[55vh]">
                <div ref={receiptRef} className="bg-white p-4 border border-[#E5E5EA] rounded-lg font-mono text-sm">
                <div className="header text-center mb-4">
                  <div className="store-name text-lg font-bold">{orgInfo?.name || user?.organization_name || 'Store'}</div>
                  {orgInfo?.address && <div className="text-xs text-[#0A0A0A]/60">{orgInfo.address}</div>}
                  {(orgInfo?.phone || orgInfo?.phone2) && (
                    <div className="text-xs text-[#0A0A0A]/60">
                      Tel: {orgInfo.phone}{orgInfo.phone2 ? ` / ${orgInfo.phone2}` : ''}
                    </div>
                  )}
                  {orgInfo?.email && <div className="text-xs text-[#0A0A0A]/60">{orgInfo.email}</div>}
                </div>
                
                <div className="divider border-t border-dashed border-[#0A0A0A]/30 my-3"></div>
                
                <div className="row flex justify-between mb-1">
                  <span className="text-[#0A0A0A]/60">Receipt #:</span>
                  <span className="font-medium">{lastSale.receipt_number}</span>
                </div>
                <div className="row flex justify-between mb-1">
                  <span className="text-[#0A0A0A]/60">Date:</span>
                  <span>{formatReceiptDate(lastSale.created_at)}</span>
                </div>
                <div className="row flex justify-between mb-1">
                  <span className="text-[#0A0A0A]/60">Cashier:</span>
                  <span>{lastSale.created_by_name}</span>
                </div>
                {lastSale.customer_name && (
                  <div className="row flex justify-between mb-1">
                    <span className="text-[#0A0A0A]/60">Customer:</span>
                    <span>{lastSale.customer_name}</span>
                  </div>
                )}
                <div className="row flex justify-between mb-1">
                  <span className="text-[#0A0A0A]/60">Payment:</span>
                  <span className="font-medium uppercase">{lastSale.payment_method || 'Cash'}</span>
                </div>
                
                <div className="divider border-t border-dashed border-[#0A0A0A]/30 my-3"></div>
                
                {lastSale.items?.map((item, idx) => (
                  <div key={idx} className="item-row my-2 pb-2 border-b border-dotted border-[#0A0A0A]/20">
                    <div className="item-name font-bold">{item.item_name}</div>
                    <div className="item-details text-xs text-[#0A0A0A]/60">SKU: {item.item_sku}</div>
                    <div className="row flex justify-between mt-1">
                      <span>{item.quantity} x {formatCurrency(item.selling_price)}</span>
                      <span>{formatCurrency(item.line_total)}</span>
                    </div>
                  </div>
                ))}
                
                <div className="divider border-t border-dashed border-[#0A0A0A]/30 my-3"></div>
                
                <div className="row flex justify-between mb-1">
                  <span>Subtotal:</span>
                  <span>{formatCurrency(lastSale.subtotal)}</span>
                </div>
                {lastSale.discount_amount > 0 && (
                  <div className="row flex justify-between mb-1 text-[#FF9500]">
                    <span>Discount{lastSale.discount_type === 'percentage' ? ` (${lastSale.discount_value}%)` : ''}:</span>
                    <span>-{formatCurrency(lastSale.discount_amount)}</span>
                  </div>
                )}
                {lastSale.points_redeemed > 0 && (
                  <div className="row flex justify-between mb-1 text-[#FFD700]">
                    <span>Points Redeemed ({lastSale.points_redeemed} pts):</span>
                    <span>-{formatCurrency(lastSale.points_discount || lastSale.points_redeemed)}</span>
                  </div>
                )}
                
                <div className="divider border-t border-solid border-[#0A0A0A] my-2"></div>
                
                <div className="total-row row flex justify-between text-lg font-bold">
                  <span>TOTAL:</span>
                  <span>{formatCurrency(lastSale.total_amount)}</span>
                </div>
                
                {lastSale.amount_paid > 0 && (
                  <>
                    <div className="row flex justify-between mt-2">
                      <span>Amount Paid:</span>
                      <span className="font-medium">{formatCurrency(lastSale.amount_paid)}</span>
                    </div>
                    <div className="row flex justify-between font-bold text-lg">
                      <span>CHANGE:</span>
                      <span>{formatCurrency(lastSale.amount_paid - lastSale.total_amount)}</span>
                    </div>
                  </>
                )}

                {/* Loyalty Points on Receipt */}
                {lastSale.customer_name && (lastSale.points_earned > 0 || lastSale.points_redeemed > 0) && (
                  <div className="mt-3 p-2 bg-[#FFD700]/10 border border-[#FFD700]/30 rounded text-center">
                    <div className="text-xs font-bold text-[#D4A800] mb-1">LOYALTY REWARDS</div>
                    {lastSale.points_earned > 0 && (
                      <div className="text-xs">Points Earned: <span className="font-bold text-[#34C759]">+{lastSale.points_earned}</span></div>
                    )}
                    {lastSale.points_redeemed > 0 && (
                      <div className="text-xs">Points Redeemed: <span className="font-bold text-[#FF9500]">-{lastSale.points_redeemed}</span></div>
                    )}
                    <div className="text-xs font-bold mt-1">Balance: {lastSale.customer_points_balance || 0} pts</div>
                  </div>
                )}
                
                <div className="footer text-center mt-4 text-xs text-[#0A0A0A]/50">
                  <div className="border-t border-dashed border-[#0A0A0A]/20 pt-3 mb-2">
                    {orgInfo?.return_policy ? (
                      <>
                        <div className="font-bold text-[#0A0A0A]/70 mb-1">RETURN POLICY</div>
                        {orgInfo.return_policy.split('\n').map((line, i) => (
                          <div key={i}>{line}</div>
                        ))}
                      </>
                    ) : null}
                  </div>
                  <div className="mt-2">{orgInfo?.receipt_tagline || 'Thank you for your purchase!'}</div>
                  <div className="mt-1">Powered by JamInventra</div>
                </div>
              </div>
              </div>

              <DialogFooter className="flex gap-2 mt-2 pt-2 border-t border-[#E5E5EA]">
                <Button variant="outline" className="w-full" onClick={() => setReceiptOpen(false)}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Barcode Scanner */}
      <BarcodeScanner 
        isOpen={scannerOpen}
        onScan={handleBarcodeScan}
        onClose={() => setScannerOpen(false)}
      />

      {/* Held Transactions Dialog */}
      <Dialog open={showHeldList} onOpenChange={setShowHeldList}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pause className="w-5 h-5 text-[#FF9500]" />
              Held Transactions ({heldTransactions.length})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {heldTransactions.length === 0 ? (
              <p className="text-center text-[#0A0A0A]/40 py-8">No held transactions</p>
            ) : (
              heldTransactions.map((held) => (
                <div key={held.id} className="flex items-center justify-between p-3 rounded-lg border border-[#E5E5EA] hover:border-[#FF9500]/30 transition-all">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{held.label}</div>
                    <div className="text-xs text-[#0A0A0A]/50">
                      {held.cart.length} item{held.cart.length !== 1 ? 's' : ''} • {formatCurrency(held.total)} • {new Date(held.heldAt).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                    </div>
                    <div className="text-xs text-[#0A0A0A]/40 truncate mt-0.5">
                      {held.cart.map(c => c.item_name).join(', ')}
                    </div>
                  </div>
                  <div className="flex gap-1 ml-2 shrink-0">
                    <Button size="sm" className="bg-[#FF9500] hover:bg-[#E68A00] h-8 px-3" onClick={() => handleRecallTransaction(held)} data-testid={`recall-held-${held.id}`}>
                      <Play className="w-3.5 h-3.5 mr-1" /> Recall
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 px-2 text-[#FF3B30] hover:text-[#FF3B30]" onClick={() => handleDeleteHeld(held.id)} data-testid={`delete-held-${held.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHeldList(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Void Sale Dialog */}
      <Dialog open={voidDialogOpen} onOpenChange={setVoidDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#FF3B30]">
              <XCircle className="w-5 h-5" />
              Void Sale
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#0A0A0A]/60">
              This will cancel the sale completely and restore all stock. 
              {user?.role === 'warehouse_staff' && ' A manager must approve this request.'}
            </p>
            {lastSale && (
              <div className="bg-[#FF3B30]/5 border border-[#FF3B30]/20 rounded p-2 text-sm">
                <div className="font-semibold">{lastSale.receipt_number}</div>
                <div>Total: {formatCurrency(lastSale.total_amount)}</div>
              </div>
            )}
            <div className="space-y-1">
              <Label>Reason for voiding *</Label>
              <Textarea 
                value={voidReason} 
                onChange={(e) => setVoidReason(e.target.value)} 
                placeholder="e.g., Customer changed mind, Wrong items scanned..."
                data-testid="void-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoidDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#FF3B30] hover:bg-[#D92D20]" onClick={handleRequestVoid} data-testid="confirm-void-btn">
              {user?.role === 'warehouse_staff' ? 'Request Void' : 'Void Sale'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog open={refundDialogOpen} onOpenChange={setRefundDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#FF9500]">
              <RotateCcw className="w-5 h-5" />
              Refund Items
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#0A0A0A]/60">
              Select items to refund. Stock will be restored.
              {user?.role === 'warehouse_staff' && ' A manager must approve this request.'}
            </p>
            <div className="space-y-2 max-h-[40vh] overflow-y-auto">
              {refundItems.map((item, idx) => (
                <div key={idx} className={`flex items-center gap-3 p-2 rounded border transition-all ${item.selected ? 'border-[#FF9500] bg-[#FF9500]/5' : 'border-[#E5E5EA]'}`}>
                  <Checkbox 
                    checked={item.selected}
                    onCheckedChange={(checked) => {
                      const updated = [...refundItems];
                      updated[idx].selected = checked;
                      setRefundItems(updated);
                    }}
                    data-testid={`refund-item-check-${idx}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{item.item_name}</div>
                    <div className="text-xs text-[#0A0A0A]/50">
                      {formatCurrency(item.selling_price)} each • Max: {item.max_qty}
                    </div>
                  </div>
                  {item.selected && (
                    <Input 
                      type="number" 
                      className="w-16 h-8 text-center text-sm"
                      value={item.refund_qty}
                      min={1}
                      max={item.max_qty}
                      onChange={(e) => {
                        const updated = [...refundItems];
                        updated[idx].refund_qty = Math.min(parseFloat(e.target.value) || 1, item.max_qty);
                        setRefundItems(updated);
                      }}
                      data-testid={`refund-qty-${idx}`}
                    />
                  )}
                </div>
              ))}
            </div>
            {refundItems.some(i => i.selected) && (
              <div className="bg-[#FF9500]/5 border border-[#FF9500]/20 rounded p-2 text-sm font-semibold text-center">
                Refund Total: {formatCurrency(refundItems.filter(i => i.selected).reduce((sum, i) => sum + i.refund_qty * i.selling_price, 0))}
              </div>
            )}
            <div className="space-y-1">
              <Label>Reason for refund *</Label>
              <Textarea 
                value={refundReason} 
                onChange={(e) => setRefundReason(e.target.value)} 
                placeholder="e.g., Defective item, Customer not satisfied..."
                data-testid="refund-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#FF9500] hover:bg-[#E68A00]" onClick={handleRequestRefund} data-testid="confirm-refund-btn">
              {user?.role === 'warehouse_staff' ? 'Request Refund' : 'Process Refund'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pending Approvals Dialog (Manager) */}
      <Dialog open={approvalsOpen} onOpenChange={setApprovalsOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#FF3B30]" />
              Pending Approvals ({pendingApprovals.length})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 overflow-y-auto flex-1">
            {pendingApprovals.length === 0 ? (
              <p className="text-center text-[#0A0A0A]/40 py-8">No pending approvals</p>
            ) : (
              pendingApprovals.map((pa) => (
                <div key={pa.id} className="border border-[#E5E5EA] rounded-lg p-3 space-y-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="font-mono font-semibold text-sm">{pa.receipt_number}</span>
                      <span className={`ml-2 text-xs font-bold px-1.5 py-0.5 rounded ${
                        pa.status === 'void_requested' ? 'bg-[#FF3B30]/10 text-[#FF3B30]' : 'bg-[#FF9500]/10 text-[#FF9500]'
                      }`}>{pa.status === 'void_requested' ? 'VOID REQUEST' : 'REFUND REQUEST'}</span>
                    </div>
                    <span className="text-sm font-mono font-semibold">{formatCurrency(pa.total_amount)}</span>
                  </div>
                  <div className="text-xs text-[#0A0A0A]/60">
                    Requested by: <span className="font-medium">{pa.requested_by_name}</span>
                    {pa.customer_name && <> • Customer: <span className="font-medium">{pa.customer_name}</span></>}
                  </div>
                  {pa.void_reason && (
                    <div className="text-xs bg-[#FF3B30]/5 p-2 rounded"><strong>Reason:</strong> {pa.void_reason}</div>
                  )}
                  {pa.refunds?.map((refund) => (
                    <div key={refund.id} className="text-xs bg-[#FF9500]/5 p-2 rounded space-y-1">
                      <div><strong>Reason:</strong> {refund.reason}</div>
                      <div><strong>Refund Amount:</strong> {formatCurrency(refund.refund_amount)}</div>
                      <div>{refund.items?.map(ri => `${ri.quantity}x ${ri.item_name}`).join(', ')}</div>
                    </div>
                  ))}
                  <div className="flex gap-2 pt-1">
                    {pa.status === 'void_requested' ? (
                      <>
                        <Button size="sm" className="bg-[#34C759] hover:bg-[#2DB14E]" onClick={() => handleApproveVoid(pa.id)} data-testid={`approve-void-${pa.id}`}>
                          <CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve Void
                        </Button>
                        <Button size="sm" variant="outline" className="text-[#FF3B30] border-[#FF3B30]" onClick={() => handleRejectVoid(pa.id)} data-testid={`reject-void-${pa.id}`}>
                          Reject
                        </Button>
                      </>
                    ) : (
                      pa.refunds?.filter(r => r.status === 'pending').map(refund => (
                        <div key={refund.id} className="flex gap-2">
                          <Button size="sm" className="bg-[#34C759] hover:bg-[#2DB14E]" onClick={() => handleApproveRefund(pa.id, refund.id)} data-testid={`approve-refund-${refund.id}`}>
                            <CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve Refund
                          </Button>
                          <Button size="sm" variant="outline" className="text-[#FF3B30] border-[#FF3B30]" onClick={() => handleRejectRefund(pa.id, refund.id)} data-testid={`reject-refund-${refund.id}`}>
                            Reject
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApprovalsOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
